// Multi-slot save browser: lists every saved game (the autosave the app
// already maintains, plus any named snapshots), lets the player load or
// delete one, and capture the current game as a new named snapshot — dev/
// utility tooling, not a game-design feature (matches debug.ts's treatment).
//
// Deliberately does NOT refresh its list every render-loop frame the way
// the HUD/inspector do: renderList() rebuilds the row DOM wholesale, and
// doing that on a ~60fps cadence is exactly the pattern that silently ate
// clicks in the control-node chips and had to be fixed (see inspector.ts's
// refreshControlNodeChips comment) — it only re-renders after an explicit
// user action (open, save, delete), all of which are already synchronous
// with the click that triggered them.

import { deleteSaveSlot, listSaveSlots, saveGameAsNewSlot, type SaveSlotMeta } from "./save";
import type { GameState } from "./state";

export interface SavesCallbacks {
  getState: () => GameState;
  onLoadSlot: (id: string) => void;
}

const panelEl = document.getElementById("saves-panel") as HTMLDivElement;
const closeBtn = document.getElementById("saves-close") as HTMLButtonElement;
const nameInput = document.getElementById("saves-name-input") as HTMLInputElement;
const saveNewBtn = document.getElementById("saves-save-new") as HTMLButtonElement;
const listEl = document.getElementById("saves-list") as HTMLDivElement;

let isOpen = false;
let callbacks: SavesCallbacks | null = null;

export function isSavesPanelOpen(): boolean {
  return isOpen;
}

export function closeSavesPanel(): void {
  isOpen = false;
  panelEl.classList.add("hidden");
}

export function openSavesPanel(): void {
  isOpen = true;
  panelEl.classList.remove("hidden");
  renderList();
}

export function toggleSavesPanel(): void {
  if (isOpen) closeSavesPanel();
  else openSavesPanel();
}

function formatRelativeTime(ts: number): string {
  const seconds = Math.round((Date.now() - ts) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function buildSlotRow(slot: SaveSlotMeta): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "save-row";

  const info = document.createElement("div");
  info.className = "save-row-info";
  const nameLine = document.createElement("div");
  const isAutosave = slot.id === "autosave";
  nameLine.innerHTML = `<strong>${escapeHtml(slot.name)}</strong>${isAutosave ? ' <span class="dim">(auto)</span>' : ""}`;
  const detailLine = document.createElement("div");
  detailLine.className = "dim";
  detailLine.textContent = `Day ${slot.day} · Score ${slot.score} · ${formatRelativeTime(slot.savedAt)}`;
  info.append(nameLine, detailLine);
  row.appendChild(info);

  const actions = document.createElement("div");
  actions.className = "save-row-actions";

  const loadBtn = document.createElement("button");
  loadBtn.className = "dismantle-btn";
  loadBtn.textContent = "Load";
  loadBtn.addEventListener("click", () => callbacks?.onLoadSlot(slot.id));
  actions.appendChild(loadBtn);

  const deleteBtn = document.createElement("button");
  deleteBtn.className = "dismantle-btn save-delete-btn";
  deleteBtn.textContent = "Delete";
  deleteBtn.addEventListener("click", () => {
    deleteSaveSlot(slot.id);
    renderList();
  });
  actions.appendChild(deleteBtn);

  row.appendChild(actions);
  return row;
}

function escapeHtml(s: string): string {
  const div = document.createElement("div");
  div.textContent = s;
  return div.innerHTML;
}

function renderList(): void {
  listEl.innerHTML = "";
  const slots = listSaveSlots();
  if (slots.length === 0) {
    const empty = document.createElement("div");
    empty.className = "dim";
    empty.textContent = "No saves yet.";
    listEl.appendChild(empty);
    return;
  }
  for (const slot of slots) listEl.appendChild(buildSlotRow(slot));
}

export function initSavesPanel(cbs: SavesCallbacks): void {
  callbacks = cbs;
  closeBtn.addEventListener("click", () => closeSavesPanel());
  saveNewBtn.addEventListener("click", () => {
    if (!callbacks) return;
    saveGameAsNewSlot(callbacks.getState(), nameInput.value);
    nameInput.value = "";
    renderList();
  });
  nameInput.addEventListener("keydown", (evt) => {
    if (evt.key === "Enter") saveNewBtn.click();
  });
}
